import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiCheckCircle, FiLoader } from 'react-icons/fi';
import { doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { db } from '../utils/firebase';
import { verifyPayment } from '../utils/paymentService';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const started = useRef(false);

  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);

  const paymentReference = useMemo(
    () => ({
      val_id: searchParams.get('val_id') || '',
      tran_id: searchParams.get('tran_id') || '',
      invoice_id: searchParams.get('invoice_id') || '',
      order_id: searchParams.get('order_id') || '',
      status: (searchParams.get('status') || '').toUpperCase(),
      amount: searchParams.get('amount') || '',
      transaction_id: searchParams.get('transaction_id') || ''
    }),
    [searchParams]
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const updateOrder = async (orderId, paymentData, verified) => {
      if (!orderId) return;

      try {
        await updateDoc(doc(db, 'orders', orderId), {
          status: 'confirmed',
          paymentStatus: paymentData.status === 'COMPLETED' ? 'paid' : 'pending',
          paymentVerified: verified,
          paymentMethod: 'sslcommerz',
          paymentGateway: 'sslcommerz',
          transactionId: paymentData.transaction_id || paymentData.invoice_id || '',
          gatewayStatus: paymentData.gateway_status || paymentData.status,
          paidAmount: Number(paymentData.charged_amount || paymentData.amount || 0),
          paymentDate: new Date(),
          updatedAt: new Date()
        });
      } catch (error) {
        console.error('Order update failed:', error);
      }
    };

    const resolvePayment = async () => {
      try {
        const hasReference =
          Boolean(paymentReference.val_id) ||
          Boolean(paymentReference.tran_id) ||
          Boolean(paymentReference.invoice_id);

        if (!hasReference) {
          if (paymentReference.status === 'COMPLETED') {
            const fallbackData = {
              invoice_id: paymentReference.invoice_id || paymentReference.tran_id,
              transaction_id: paymentReference.transaction_id,
              amount: paymentReference.amount,
              charged_amount: paymentReference.amount,
              payment_method: 'sslcommerz',
              status: 'COMPLETED',
              gateway_status: paymentReference.status,
              metadata: {
                order_id: paymentReference.order_id
              },
              date: new Date().toISOString(),
              isPaid: true
            };

            await updateOrder(paymentReference.order_id, fallbackData, false);
            clearCart();
            setOrderDetails(fallbackData);
            setVerificationStatus('success');
            toast.success('Payment completed successfully.');
            return;
          }

          setVerificationStatus('error');
          toast.error('Payment reference missing. Please contact support.');
          return;
        }

        const verificationResult = await verifyPayment(paymentReference);

        if (!verificationResult.success || !verificationResult.data) {
          setVerificationStatus('error');
          toast.error(verificationResult.error || 'Payment verification failed.');
          return;
        }

        const paymentData = verificationResult.data;
        const orderId = paymentData.metadata?.order_id || paymentReference.order_id;

        setOrderDetails(paymentData);

        if (paymentData.status === 'COMPLETED') {
          await updateOrder(orderId, paymentData, true);
          clearCart();
          setVerificationStatus('success');
          toast.success('Payment verified and order confirmed.');
          return;
        }

        if (paymentData.status === 'PENDING') {
          await updateOrder(orderId, paymentData, true);
          setVerificationStatus('pending');
          toast('Payment is pending confirmation.');
          return;
        }

        setVerificationStatus('error');
        toast.error('Payment could not be confirmed.');
      } catch (error) {
        console.error('PaymentSuccess error:', error);
        setVerificationStatus('error');
        toast.error('Failed to verify payment status.');
      } finally {
        setIsVerifying(false);
      }
    };

    resolvePayment();
  }, [paymentReference, clearCart]);

  if (isVerifying) {
    return (
      <div className="sc-container py-12">
        <div className="mx-auto max-w-xl sc-soft-card p-10 text-center">
          <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-600">
            <FiLoader size={26} className="animate-spin" />
          </div>
          <h1 className="text-2xl font-black text-[#1A1A1A]">Verifying Payment</h1>
          <p className="mt-2 text-sm text-slate-500">
            Please wait while we verify your SSLCommerz transaction.
          </p>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className="sc-container py-12">
        <div className="mx-auto max-w-xl sc-soft-card p-10 text-center">
          <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
            <FiAlertCircle size={28} />
          </div>
          <h1 className="text-2xl font-black text-[#1A1A1A]">Payment Verification Failed</h1>
          <p className="mt-2 text-sm text-slate-500">
            We could not verify the payment. Please contact support or check your orders.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Link
              to="/orders"
              className="inline-flex items-center justify-center rounded-xl bg-[#DB4444] px-5 py-3 text-sm font-bold text-white hover:bg-[#b93333]"
            >
              View Orders
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const title = verificationStatus === 'pending' ? 'Payment Pending' : 'Payment Successful';
  const badgeTone =
    verificationStatus === 'pending'
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700';

  return (
    <div className="sc-container py-12">
      <div className="mx-auto max-w-xl sc-soft-card p-10 text-center">
        <div className={`mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full ${badgeTone}`}>
          {verificationStatus === 'pending' ? <FiAlertCircle size={28} /> : <FiCheckCircle size={28} />}
        </div>

        <h1 className="text-2xl font-black text-[#1A1A1A]">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">
          {verificationStatus === 'pending'
            ? 'Your payment is being processed by SSLCommerz. We will confirm shortly.'
            : 'Your order has been confirmed and is now in processing.'}
        </p>

        {orderDetails && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left text-sm">
            <h3 className="mb-3 font-bold text-[#1A1A1A]">Payment Details</h3>
            <div className="space-y-1.5 text-slate-600">
              {orderDetails.invoice_id && (
                <p>
                  <span className="font-semibold">Transaction Ref:</span> {orderDetails.invoice_id}
                </p>
              )}
              {orderDetails.transaction_id && (
                <p>
                  <span className="font-semibold">Gateway Transaction:</span> {orderDetails.transaction_id}
                </p>
              )}
              {orderDetails.amount !== undefined && (
                <p>
                  <span className="font-semibold">Amount:</span> BDT {Number(orderDetails.amount).toFixed(2)}
                </p>
              )}
              <p>
                <span className="font-semibold">Status:</span> {orderDetails.status}
              </p>
              {orderDetails.gateway_status && (
                <p>
                  <span className="font-semibold">Gateway Status:</span> {orderDetails.gateway_status}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Link
            to={orderDetails?.metadata?.order_id ? `/track-order/${orderDetails.metadata.order_id}` : '/orders'}
            className="inline-flex items-center justify-center rounded-xl bg-[#DB4444] px-5 py-3 text-sm font-bold text-white hover:bg-[#b93333]"
          >
            Track Order
            <FiArrowRight className="ml-2" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            Continue Shopping
            <FiArrowRight className="ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
